import {onMounted, onUnmounted, ref, watch} from 'vue'
import {format} from 'date-fns'
import {attachments, isTransactionEntryLineEmpty, useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {ORG_SELECTED} from '@/types/events'
import {TransactionEntryLine} from '@/types/transaction/TransactionEntryLine'
import {useUserPrefs} from '@/user-prefs/userPrefs.service'
import {Draft, DRAFT_TYPE, useDrafts} from '@/draft/drafts.service'
import {TRANSACTION_DRAFT_CREATED, TRANSACTION_DRAFT_DELETED, TRANSACTION_DRAFT_LOADED, TRANSACTION_DRAFT_UPDATED} from '@/types/transaction/TransactionEvent'
import {registerIncomingDocuments} from '@/services/document.service'
import {notifySuccess} from '@/services/infrastructure/notification.service'
import {entryLinesToTransactionLines} from '@/transaction/transaction.mapper'
import {v4 as uuidv4} from 'uuid'

const defaultTransactionDraftName = (isAutoSave?: boolean) => {
    const formattedName = format(new Date(), 'dd.MM.yyyy\'_\'HH:mm:ss')
    return !isAutoSave ? formattedName + '' : formattedName + '_autosave'
}

const {onEvent} = useEmitter()

const {saveDraft, updateDraft, deleteDraft, fetchDrafts, loading} = useDrafts()

const {autoSaveDraft, skipAutoSaveDraft} = useUserPrefs()

let autoSaveTimeoutId: ReturnType<typeof setTimeout>
let autoSaveScheduled = false

const draftUuid = ref<string>()

export const useTransactionDrafts = () => {
    const {transactionEntryLines} = useTransactionCreator()

    const drafts = ref<Draft[]>([])

    const parseDraft = (draft: any): { documentsMetas: string [], entryLines: TransactionEntryLine[] } => {
        // TODO do not need to parse only map/downcast
        const parsedEntries = draft
        const documentsMetas = parsedEntries.documentsMetas || []
        if (!parsedEntries.linesWithUuid || !Array.isArray(parsedEntries.linesWithUuid)) {
            return {documentsMetas, entryLines: []}
        }
        const entriesLines = parsedEntries.linesWithUuid.map((entry: any) => {
            return {
                ...entry,
                date: new Date(entry.date),
                invoiceDueDate: entry.invoiceDueDate ? new Date(entry.invoiceDueDate) : undefined
            }
        })

        return {documentsMetas, entryLines: entriesLines}
    }


    const transactionDraftsList = async (): Promise<Draft[]> => {

        return await fetchDrafts(DRAFT_TYPE.TRANSACTION).then(drafts => {
            return (drafts as Draft[]).map(draft => {
                const parsedDraftContent = parseDraft(draft.content)
                return {...draft, content: parsedDraftContent}
            })
        })
    }

    const debouncedAutoSave = async () => {
        if (!autoSaveDraft.value || isTransactionEntryLineEmpty.value || skipAutoSaveDraft.value || autoSaveScheduled) return
        if (autoSaveTimeoutId) clearTimeout(autoSaveTimeoutId)
        autoSaveScheduled = true

        await registerIncomingDocuments(attachments.value)
        autoSaveTimeoutId = setTimeout(async () => {
            const transactionLines = entryLinesToTransactionLines(transactionEntryLines.value)
            const linesWithUuid = transactionLines.map(line => ({...line, uuid: uuidv4()}))
            const draft = {
                name: defaultTransactionDraftName(true),
                type: DRAFT_TYPE.TRANSACTION,
                content: {linesWithUuid, documentsMetas: attachments.value.map(file => file.uuid)}
            }

            if (draftUuid.value) {
                await updateDraft({...draft, uuid: draftUuid.value})
                emitter.emit(TRANSACTION_DRAFT_UPDATED)
            } else {
                const resp = await saveDraft(draft)
                emitter.emit(TRANSACTION_DRAFT_CREATED)
                emitter.emit(TRANSACTION_DRAFT_LOADED, resp.uuid)
            }

            notifySuccess({title: 'Opprettet', description: `Kladden ${draft.name} ble automatisk opprettet.`})
            skipAutoSaveDraft.value = false
            autoSaveScheduled = false
        }, 1000 * 10)
    }

    watch(transactionEntryLines, debouncedAutoSave, {deep: true})

    const fetchTransactionsDrafts = async () => {
        drafts.value = await transactionDraftsList()
    }

    const getTransactionDraft = (uuid: string) => {
        return drafts.value.find(draft => draft.uuid === uuid)
    }

    onEvent(ORG_SELECTED, fetchTransactionsDrafts)
    onEvent(TRANSACTION_DRAFT_CREATED, fetchTransactionsDrafts)
    onEvent(TRANSACTION_DRAFT_UPDATED, fetchTransactionsDrafts)
    onEvent(TRANSACTION_DRAFT_DELETED, fetchTransactionsDrafts)

    onMounted(() => fetchTransactionsDrafts())
    onUnmounted(() => clearTimeout(autoSaveTimeoutId))

    return {
        drafts,
        loading,
        autoSaveDraft,
        skipAutoSaveDraft,
        saveDraft,
        updateDraft,
        deleteDraft,
        transactionDraftsList,
        getTransactionDraft
    }
}

export {
    defaultTransactionDraftName,
    draftUuid
}